/*
 * Copyright 2023 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { PalantirApiError } from "@osdk/shared.net.errors";
import { createFetchHeaderMutator, createFetchOrThrow, createRetryingFetch } from "@osdk/shared.net.fetch";

// eslint-disable-next-line @typescript-eslint/consistent-type-imports

export const USER_AGENT_HEADER = "Fetch-User-Agent";
export function createSharedClientContext(baseUrl, tokenProvider, userAgent, fetchFn = fetch, customHeaders) {
  if (baseUrl.length === 0) {
    throw new Error("baseUrl cannot be empty");
  }
  const parsedBaseUrl = new URL(baseUrl);
  if (!parsedBaseUrl.pathname.endsWith("/")) {
    parsedBaseUrl.pathname += "/";
  }
  const normalizedBaseUrl = parsedBaseUrl.toString();
  const retryingFetchWithAuthOrThrow = createFetchHeaderMutator(createRetryingFetch(createFetchOrThrow(fetchFn)), async headers => {
    if (customHeaders != null) {
      for (const [key, value] of Object.entries(customHeaders)) {
        if (key.toLowerCase() !== USER_AGENT_HEADER.toLowerCase()) {
          headers.set(key, value);
        }
      }
    }
    const token = await tokenProvider();
    headers.set("Authorization", `Bearer ${token}`);
    const customUserAgent = customHeaders ? Object.entries(customHeaders).find(([k]) => k.toLowerCase() === USER_AGENT_HEADER.toLowerCase())?.[1] : undefined;
    headers.set(USER_AGENT_HEADER, [headers.get(USER_AGENT_HEADER), userAgent, customUserAgent].filter(x => x && x?.length > 0).join(" "));
    return headers;
  });

  // because this is async await it preserves stack traces, which the retrying fetch does not

  return {
    baseUrl: normalizedBaseUrl,
    fetch: async (input, init) => {
      try {
        return await retryingFetchWithAuthOrThrow(input, init);
      } catch (error) {
        const betterError = error instanceof PalantirApiError ? new PalantirApiError(error.message, error.errorName, error.errorCode, error.errorDescription, error.statusCode, error.errorInstanceId, error.parameters) : new Error(`Captured stack trace for error: ${error.message ?? error}`);
        betterError.cause = error;
        throw betterError;
      }
    },
    tokenProvider
  };
}